import { sumBy, round } from 'lodash';

type RealisationAllocation = {
  commitmentId: string;
  allocationPercentage: number;
  receivedConsiderationLcy: number;
  earnoutConsiderationLcy: number;
  escrowConsiderationLcy: number;
  withholdingTaxPayableLcy: number;
  unitsAllocated: number;
};

type BalanceRealisationAllocationsInput<T extends RealisationAllocation> = {
  allocations: Array<T>;
  totals: {
    totalReceivedConsiderationLcy: number;
    totalEarnoutLcy: number;
    totalEscrowLcy: number;
    totalWithholdingTaxLcy: number;
    totalUnits: number;
  };
};

export default function balanceRealisationAllocations<
  T extends RealisationAllocation,
>({ allocations, totals }: BalanceRealisationAllocationsInput<T>): Array<T> {
  if (allocations.length === 0) {
    return allocations;
  }

  const {
    totalReceivedConsiderationLcy,
    totalEarnoutLcy,
    totalEscrowLcy,
    totalWithholdingTaxLcy,
    totalUnits,
  } = totals;

  const sumReceivedConsideration = round(
    sumBy(allocations, 'receivedConsiderationLcy'),
    2,
  );
  const sumEarnout = round(sumBy(allocations, 'earnoutConsiderationLcy'), 2);
  const sumEscrow = round(sumBy(allocations, 'escrowConsiderationLcy'), 2);
  const sumWithholdingTax = round(
    sumBy(allocations, 'withholdingTaxPayableLcy'),
    2,
  );
  const sumUnits = round(sumBy(allocations, 'unitsAllocated'), 2);

  const receivedDiff = round(
    totalReceivedConsiderationLcy - sumReceivedConsideration,
    2,
  );
  const earnoutDiff = round(totalEarnoutLcy - sumEarnout, 2);
  const escrowDiff = round(totalEscrowLcy - sumEscrow, 2);
  const withholdingTaxDiff = round(
    totalWithholdingTaxLcy - sumWithholdingTax,
    2,
  );
  const unitsDiff = round(totalUnits - sumUnits, 2);

  const largestAllocation = allocations.reduce((largest, current) =>
    current.allocationPercentage > largest.allocationPercentage
      ? current
      : largest,
  );

  const allocationWithLargestWitholdingTax = allocations.reduce(
    (largest, current) =>
      current.withholdingTaxPayableLcy > largest.withholdingTaxPayableLcy
        ? current
        : largest,
  );

  return allocations.map((allocation) => {
    if (allocation.commitmentId === largestAllocation.commitmentId) {
      return {
        ...allocation,
        receivedConsiderationLcy: round(
          allocation.receivedConsiderationLcy + receivedDiff,
          2,
        ),
        earnoutConsiderationLcy: round(
          allocation.earnoutConsiderationLcy + earnoutDiff,
          2,
        ),
        escrowConsiderationLcy: round(
          allocation.escrowConsiderationLcy + escrowDiff,
          2,
        ),
        unitsAllocated: round(allocation.unitsAllocated + unitsDiff, 2),
      };
    } else if (
      allocation.commitmentId ===
      allocationWithLargestWitholdingTax.commitmentId
    ) {
      return {
        ...allocation,
        withholdingTaxPayableLcy: round(
          allocation.withholdingTaxPayableLcy + withholdingTaxDiff,
          2,
        ),
      };
    }
    return allocation;
  });
}
